import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PayInvoiceDialog } from '@/features/cards/CardsPage';
import type { InvoiceDetail } from '@/features/cards/CardsPage';
import { PayInstallmentDialog } from '@/features/debts/DebtsPage';
import type { LoanDetail } from '@/features/debts/types';
import type { ReferenceData } from '@/features/transactions/types';

/**
 * Pagar fatura e parcela de dívida SEM sair de A pagar — RF-PAG-04.
 *
 * ================================================================
 * MESMA AÇÃO, NÃO MESMO BOTÃO
 * ================================================================
 *
 * A tela listava fatura e parcela ao lado das contas, mas o botão dizia "Ver
 * fatura" / "Ver dívida" e mandava para outra tela. A maior linha da lista não
 * tinha ação onde a decisão é tomada.
 *
 * O que NÃO dá para fazer é tratá-las como baixa comum, e o motivo é contábil,
 * não de gosto:
 *
 *   Fatura de cartão   pagar é TRANSFERÊNCIA (RN-22). Uma baixa comum criaria
 *                      despesa nova sobre compras já reconhecidas, e o mês
 *                      apareceria com o dobro do tamanho.
 *   Parcela de dívida  só os JUROS são despesa (RN-61); o principal abate o
 *                      saldo devedor. Uma baixa comum trataria a parcela
 *                      inteira como gasto do mês.
 *
 * Então este componente não reimplementa nada: ele busca o registro e abre o
 * MESMO diálogo das telas de Cartões e Dívidas. As regras moram num lugar só —
 * duplicá-las aqui seria garantir que as duas cópias divergissem.
 */
export function PayFromObligations({
  item,
  reference,
  selectedIds,
  onClose,
  onPaid,
}: {
  item: { kind: 'card_invoice' | 'loan_installment'; id: number; loan_id: number | null };
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onPaid: () => void;
}) {
  const ehFatura = item.kind === 'card_invoice';

  const fatura = useQuery({
    queryKey: ['invoice', item.id, selectedIds],
    queryFn: () =>
      api.get<{ data: InvoiceDetail }>(`/card-invoices/${item.id}`, { entityIds: selectedIds }),
    enabled: ehFatura,
    select: (r) => r.data,
  });

  const divida = useQuery({
    queryKey: ['debt', item.loan_id, selectedIds],
    queryFn: () =>
      api.get<{ data: LoanDetail }>(`/debts/${item.loan_id}`, { entityIds: selectedIds }),
    enabled: !ehFatura && item.loan_id !== null,
    select: (r) => r.data,
  });

  const carregando = ehFatura ? fatura.isPending : divida.isPending;
  const erro = ehFatura ? fatura.isError : divida.isError;

  if (carregando || erro) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 text-sm shadow-xl dark:border-slate-800 dark:bg-slate-900">
          {erro ? (
            <>
              <p className="text-rose-700 dark:text-rose-400">
                Não foi possível carregar {ehFatura ? 'a fatura' : 'a dívida'}.
              </p>
              <button
                onClick={onClose}
                className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700"
              >
                Fechar
              </button>
            </>
          ) : (
            <p className="text-slate-500 dark:text-slate-400">Carregando…</p>
          )}
        </div>
      </div>
    );
  }

  if (ehFatura && fatura.data) {
    return (
      <PayInvoiceDialog
        invoice={fatura.data}
        selectedIds={selectedIds}
        onClose={onClose}
        onPaid={onPaid}
      />
    );
  }

  /**
   * A PARCELA vem do detalhe da dívida, e pode não estar mais lá.
   *
   * Entre abrir a lista e clicar em pagar, alguém pode ter pago a parcela por
   * outra tela ou excluído a dívida. Recusar com a razão é melhor do que abrir
   * um diálogo em branco.
   */
  const parcela = divida.data?.installments.find((p) => p.id === item.id);

  if (!divida.data || !parcela) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 text-sm shadow-xl dark:border-slate-800 dark:bg-slate-900">
          <p className="text-slate-700 dark:text-slate-300">
            Esta parcela não está mais em aberto — pode ter sido paga em outra tela.
          </p>
          <button
            onClick={onPaid}
            className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700"
          >
            Atualizar a lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <PayInstallmentDialog
      loan={divida.data}
      installment={parcela}
      reference={reference}
      selectedIds={selectedIds}
      onClose={onClose}
      onPaid={onPaid}
    />
  );
}
